import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store/store';

export type AllocationDraftLine = {
  key: string;
  sku: string;
  name?: string;
  track_serial: boolean;
  qty: number; // for serialized lines, qty must be 1
  serial_code?: string | null;
};

export type AddNonSerializedLinePayload = {
  deviceTempId: string;
  sku: string;
  name?: string;
  qty: number;
};

export type AddSerializedLinePayload = {
  deviceTempId: string;
  sku: string;
  name?: string;
  serial_code?: string | null;
};

export type AllocationsDraftState = {
  byDevice: Record<string, AllocationDraftLine[]>; // key = device.tempId
};

const initialState: AllocationsDraftState = {
  byDevice: {},
};

const allocationsDraftSlice = createSlice({
  name: 'allocationsDraft',
  initialState,
  reducers: {
    addNonSerializedLine(state, action: PayloadAction<AddNonSerializedLinePayload>) {
      const { deviceTempId, sku, name, qty } = action.payload;
      const list = state.byDevice[deviceTempId] ?? [];
      list.push({ key: nanoid(), sku, name, track_serial: false, qty: Math.max(1, Math.floor(qty)) });
      state.byDevice[deviceTempId] = list;
    },
    addSerializedLine(
      state,
      action: PayloadAction<AddSerializedLinePayload>,
    ) {
      const { deviceTempId, sku, name, serial_code } = action.payload;
      const list = state.byDevice[deviceTempId] ?? [];
      list.push({ key: nanoid(), sku, name, track_serial: true, qty: 1, serial_code: serial_code || null });
      state.byDevice[deviceTempId] = list;
    },
    removeLine(state, action: PayloadAction<{ deviceTempId: string; key: string }>) {
      const { deviceTempId, key } = action.payload;
      const list = state.byDevice[deviceTempId] ?? [];
      state.byDevice[deviceTempId] = list.filter((l) => l.key !== key);
    },
    clearDraftForDevice(state, action: PayloadAction<string>) {
      delete state.byDevice[action.payload];
    },
  },
});

export const { addNonSerializedLine, addSerializedLine, removeLine, clearDraftForDevice } =
  allocationsDraftSlice.actions;

export default allocationsDraftSlice.reducer;

export const selectDraftLinesByDevice = (deviceTempId: string) => (state: RootState) =>
  (state as any)?.allocationsDraft?.byDevice?.[deviceTempId] ?? [];

export const selectAnyDraftExists = (state: RootState) => {
  const byDevice = (state as any)?.allocationsDraft?.byDevice ?? {};
  return Object.values(byDevice).some((arr) => Array.isArray(arr) && arr.length > 0);
};
